import React, { useState, useEffect } from "react";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";

function SalesTeams() {
  const { hasPermission } = useAuth();
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRegion, setFilterRegion] = useState("");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    teamLead: "",
    members: [],
    region: "",
    target: "",
    targetPeriod: "monthly",
  });

  const isAdmin = hasPermission("goals", "delete");
  const canCreate = hasPermission("goals", "create");
  const canUpdate = hasPermission("goals", "update");

  useEffect(() => {
    fetchTeams();
    fetchSalesEmployees();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (filterRegion) params.append("region", filterRegion);

      const response = await api.get(`/sales-teams?${params}`);
      setTeams(response.data.teams || response.data);
    } catch (error) {
      console.error("Error fetching sales teams:", error);
      alert("Error fetching sales teams");
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesEmployees = async () => {
    try {
      const response = await api.get("/sales-teams/employees/sales");
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching sales employees:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.teamLead) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);

      if (editingTeam) {
        await api.put(`/sales-teams/${editingTeam._id}`, formData);
        alert("Sales team updated successfully!");
      } else {
        await api.post("/sales-teams", formData);
        alert("Sales team created successfully!");
      }

      setShowModal(false);
      setEditingTeam(null);
      resetForm();
      fetchTeams();
    } catch (error) {
      console.error("Error saving sales team:", error);
      alert(error.response?.data?.message || "Error saving sales team");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name,
      description: team.description || "",
      teamLead: team.teamLead._id,
      members: team.members.map((m) => m._id),
      region: team.region || "",
      target: team.target || "",
      targetPeriod: team.targetPeriod || "monthly",
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this sales team?")) {
      return;
    }

    try {
      await api.delete(`/sales-teams/${id}`);
      alert("Sales team deleted successfully!");
      fetchTeams();
    } catch (error) {
      console.error("Error deleting sales team:", error);
      alert("Error deleting sales team");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      teamLead: "",
      members: [],
      region: "",
      target: "",
      targetPeriod: "monthly",
    });
  };

  const handleMemberToggle = (employeeId) => {
    setFormData((prev) => ({
      ...prev,
      members: prev.members.includes(employeeId)
        ? prev.members.filter((id) => id !== employeeId)
        : [...prev.members, employeeId],
    }));
  };

  const filteredTeams = teams.filter((team) => {
    const matchesSearch =
      !searchTerm ||
      team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      team.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      team.region?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRegion = !filterRegion || team.region === filterRegion;

    return matchesSearch && matchesRegion;
  });

  const regions = [
    ...new Set(teams.map((team) => team.region).filter(Boolean)),
  ];

  return (
    <div className="container">
      <div className="flex-between mb-20">
        <h1>Sales Teams Management</h1>
        {canCreate && (
          <button
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            Create Sales Team
          </button>
        )}
      </div>

      {/* Search and Filter Section */}
      <div className="card" style={{ marginBottom: "20px", padding: "15px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr auto",
            gap: "15px",
            alignItems: "end",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "500",
              }}
            >
              Search Teams
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by team name, description, or region..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "500",
              }}
            >
              Filter by Region
            </label>
            <select
              className="form-control"
              value={filterRegion}
              onChange={(e) => setFilterRegion(e.target.value)}
            >
              <option value="">All Regions</option>
              {regions.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </div>

          <button
            className="btn btn-secondary"
            onClick={() => {
              setSearchTerm("");
              setFilterRegion("");
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Teams List */}
      {loading ? (
        <div className="card" style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading sales teams...</p>
        </div>
      ) : (
        <div>
          {filteredTeams.length === 0 ? (
            <div className="card text-center">
              <p>No sales teams found</p>
            </div>
          ) : (
            filteredTeams.map((team) => (
              <div
                key={team._id}
                className="card"
                style={{ marginBottom: "20px" }}
              >
                <div className="flex-between">
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "15px",
                        marginBottom: "10px",
                      }}
                    >
                      <h3 style={{ margin: 0, color: "#667eea" }}>
                        {team.name}
                      </h3>
                      <span
                        style={{
                          padding: "4px 12px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#fff",
                          backgroundColor: team.isActive
                            ? "#28a745"
                            : "#dc3545",
                        }}
                      >
                        {team.isActive ? "ACTIVE" : "INACTIVE"}
                      </span>
                    </div>

                    {team.description && (
                      <p
                        style={{
                          fontSize: "14px",
                          color: "#555",
                          marginTop: "10px",
                        }}
                      >
                        {team.description}
                      </p>
                    )}

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: "15px",
                        marginTop: "15px",
                      }}
                    >
                      <div>
                        <p
                          style={{
                            fontSize: "12px",
                            color: "#999",
                            marginBottom: "5px",
                          }}
                        >
                          Team Lead
                        </p>
                        <p style={{ fontSize: "14px", color: "#333" }}>
                          {team.teamLead?.firstName} {team.teamLead?.lastName}
                          <span style={{ fontSize: "12px", color: "#666" }}>
                            ({team.teamLead?.employeeId})
                          </span>
                        </p>
                      </div>

                      <div>
                        <p
                          style={{
                            fontSize: "12px",
                            color: "#999",
                            marginBottom: "5px",
                          }}
                        >
                          Members
                        </p>
                        <p style={{ fontSize: "14px", color: "#333" }}>
                          {team.memberCount || team.members?.length || 0}{" "}
                          members
                        </p>
                      </div>

                      {team.region && (
                        <div>
                          <p
                            style={{
                              fontSize: "12px",
                              color: "#999",
                              marginBottom: "5px",
                            }}
                          >
                            Region
                          </p>
                          <p style={{ fontSize: "14px", color: "#333" }}>
                            {team.region}
                          </p>
                        </div>
                      )}

                      {team.target && (
                        <div>
                          <p
                            style={{
                              fontSize: "12px",
                              color: "#999",
                              marginBottom: "5px",
                            }}
                          >
                            Target
                          </p>
                          <p style={{ fontSize: "14px", color: "#333" }}>
                            ₹{team.target.toLocaleString()} ({team.targetPeriod}
                            )
                          </p>
                        </div>
                      )}
                    </div>

                    {team.members && team.members.length > 0 && (
                      <div style={{ marginTop: "15px" }}>
                        <p
                          style={{
                            fontSize: "12px",
                            color: "#999",
                            marginBottom: "8px",
                          }}
                        >
                          Team Members
                        </p>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                          }}
                        >
                          {team.members.slice(0, 5).map((member) => (
                            <span
                              key={member._id}
                              style={{
                                padding: "4px 10px",
                                background: "#e3f2fd",
                                borderRadius: "8px",
                                fontSize: "13px",
                                color: "#1976d2",
                              }}
                            >
                              {member.firstName} {member.lastName}
                            </span>
                          ))}
                          {team.members.length > 5 && (
                            <span
                              style={{
                                padding: "4px 10px",
                                background: "#f5f5f5",
                                borderRadius: "8px",
                                fontSize: "13px",
                                color: "#666",
                              }}
                            >
                              +{team.members.length - 5} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {(canUpdate || isAdmin) && (
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        marginLeft: "20px",
                      }}
                    >
                      <button
                        onClick={() => handleEdit(team)}
                        className="btn btn-secondary"
                        style={{ padding: "8px 16px" }}
                      >
                        Edit
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => handleDelete(team._id)}
                          className="btn"
                          style={{
                            padding: "8px 16px",
                            background: "#dc3545",
                            color: "white",
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div
            className="modal-content"
            style={{ maxHeight: "90vh", overflowY: "auto", maxWidth: "600px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "20px",
                borderBottom: "1px solid #eee",
                marginBottom: "20px",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "24px", fontWeight: "600" }}>
                {editingTeam ? "Edit Sales Team" : "Create Sales Team"}
              </h2>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: "0 20px" }}>
              <div className="form-group">
                <label
                  style={{
                    fontWeight: "600",
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  Team Name <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Enter team name"
                  required
                />
              </div>

              <div className="form-group">
                <label
                  style={{
                    fontWeight: "600",
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  Description
                </label>
                <textarea
                  className="form-control"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Enter team description"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label
                  style={{
                    fontWeight: "600",
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  Team Lead <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <select
                  className="form-control"
                  value={formData.teamLead}
                  onChange={(e) =>
                    setFormData({ ...formData, teamLead: e.target.value })
                  }
                  required
                >
                  <option value="">Select Team Lead</option>
                  {employees.map((emp) => (
                    <option key={emp._id} value={emp._id}>
                      {emp.firstName} {emp.lastName} ({emp.employeeId}) -{" "}
                      {emp.role}
                    </option>
                  ))}
                </select>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "15px",
                }}
              >
                <div className="form-group">
                  <label
                    style={{
                      fontWeight: "600",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    Region
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.region}
                    onChange={(e) =>
                      setFormData({ ...formData, region: e.target.value })
                    }
                    placeholder="Enter region"
                  />
                </div>

                <div className="form-group">
                  <label
                    style={{
                      fontWeight: "600",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    Target Period
                  </label>
                  <select
                    className="form-control"
                    value={formData.targetPeriod}
                    onChange={(e) =>
                      setFormData({ ...formData, targetPeriod: e.target.value })
                    }
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label
                  style={{
                    fontWeight: "600",
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  Target Amount
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.target}
                  onChange={(e) =>
                    setFormData({ ...formData, target: e.target.value })
                  }
                  placeholder="Enter target amount"
                  min="0"
                />
              </div>

              {/* Team Members Selection */}
              <div className="form-group">
                <label
                  style={{
                    fontWeight: "600",
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  Team Members ({formData.members.length} selected)
                </label>
                <div
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    maxHeight: "200px",
                    overflowY: "auto",
                    padding: "12px",
                    background: "#fafafa",
                  }}
                >
                  {employees
                    .filter((emp) => emp._id !== formData.teamLead)
                    .map((emp) => (
                      <div
                        key={emp._id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "8px",
                          marginBottom: "8px",
                          background: "white",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.members.includes(emp._id)}
                          onChange={() => handleMemberToggle(emp._id)}
                          style={{ marginRight: "10px", cursor: "pointer" }}
                        />
                        <label
                          style={{
                            cursor: "pointer",
                            flex: 1,
                            margin: 0,
                            fontSize: "13px",
                          }}
                        >
                          <span style={{ fontWeight: "500" }}>
                            {emp.firstName} {emp.lastName}
                          </span>
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#666",
                              marginLeft: "8px",
                            }}
                          >
                            ({emp.employeeId}) - {emp.role}
                          </span>
                        </label>
                      </div>
                    ))}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                  marginTop: "25px",
                  paddingTop: "20px",
                  borderTop: "1px solid #eee",
                  paddingBottom: "20px",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingTeam(null);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading
                    ? "Saving..."
                    : editingTeam
                    ? "Update Team"
                    : "Create Team"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SalesTeams;
